import React, { useEffect } from 'react';
import { Modal, Form, InputNumber, Select, Input, Row, Col, message } from 'antd';
import { financeService, SKUCostConfig } from '../../services/financeService';

interface Props {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    editingItem: Partial<SKUCostConfig> | null;
    fixedSkuId?: number; // If provided, hidden/disabled SKU selection for creation
}

export const CostConfigModal: React.FC<Props> = ({ visible, onCancel, onSuccess, editingItem, fixedSkuId }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            if (editingItem) {
                form.setFieldsValue(editingItem);
            } else {
                form.resetFields();
                if (fixedSkuId) {
                    form.setFieldsValue({ seller_sku: fixedSkuId });
                }
            }
        }
    }, [visible, editingItem, fixedSkuId, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingItem && editingItem.id) {
                await financeService.updateCostConfig(editingItem.id, values);
                message.success('更新成功');
            } else {
                await financeService.createCostConfig(values);
                message.success('创建成功');
            }
            onSuccess();
        } catch (error) {
            console.error(error);
            message.error('保存失败');
        }
    };

    return (
        <Modal
            title={editingItem ? "编辑成本配置" : "新增成本配置"}
            open={visible}
            onOk={handleSave}
            onCancel={onCancel}
            width={600}
        >
            <Form form={form} layout="vertical">
                {(!editingItem && !fixedSkuId) && (
                    <Form.Item name="seller_sku" label="Seller SKU ID" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} placeholder="输入SKU ID (临时)" />
                    </Form.Item>
                )}
                {/* 如果固定了SKU ID，且是新增模式，隐藏字段但保留值 */}
                {(fixedSkuId && !editingItem) && (
                    <Form.Item name="seller_sku" style={{ display: 'none' }}>
                        <InputNumber />
                    </Form.Item>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="purchase_cost" label="采购单价" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} precision={2} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="purchase_currency" label="币种" initialValue="CNY">
                            <Select>
                                <Select.Option value="CNY">CNY</Select.Option>
                                <Select.Option value="USD">USD</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="shipping_to_fba" label="头程运费/件">
                            <InputNumber style={{ width: '100%' }} precision={2} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="exchange_rate_to_usd" label="汇率(对USD)" initialValue={7.1}>
                            <InputNumber style={{ width: '100%' }} precision={4} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="packaging_cost" label="包装成本/件">
                            <InputNumber style={{ width: '100%' }} precision={2} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="other_cost" label="其他成本/件">
                            <InputNumber style={{ width: '100%' }} precision={2} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="estimated_fba_fee" label="预估FBA配送费/件">
                            <InputNumber style={{ width: '100%' }} precision={2} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="referral_fee_rate" label="预估佣金比率 (0.15=15%)" initialValue={0.15}>
                            <InputNumber style={{ width: '100%' }} precision={4} step={0.01} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="notes" label="备注">
                    <Input.TextArea />
                </Form.Item>
            </Form>
        </Modal>
    );
};
